import clsx from 'clsx';
import React, { HTMLAttributes } from 'react';
import { useCounter } from 'react-use';

const states = {
  default: `border-action-outline-secondary-enabled bg-interaction-fill-search`,
  disabled: `border-action-outline-secondary-active bg-action-fill-secondary-active`,
};

export type DropPaneProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onDrop' | 'onDragEnter' | 'onDragLeave' | 'onDragOver'
> & {
  disabled?: boolean;
  /**
   * Note that if you attempt to console.log the dataTransfer object, both dataTransfer.files and dataTransfer.items
   * will appear to be empty
   * @param dataTransfer
   */
  onDrop: (dataTransfer: DataTransfer) => void;
  state?: keyof typeof states;
};

export const DropPane: React.FC<DropPaneProps> = ({
  children,
  disabled,
  onDrop,
  state = 'default',
  className,
  ...props
}) => {
  const [count, { inc, dec, set }] = useCounter();

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center p-4 w-full border-1 border-dashed rounded-lg hover:border-action-outline-secondary-hover hover:bg-interaction-fill-search',
        states[state],
        !disabled && !!count && `bg-gray-100`,
        className,
      )}
      onDragEnter={() => inc()}
      onDragLeave={() => dec()}
      onDrop={e => {
        set(0);
        e.preventDefault();
        if (!disabled) {
          onDrop(e.dataTransfer);
        }
      }}
      onKeyDown={e => e.currentTarget}
      onDragOver={e => e.preventDefault()}
      {...props}
    >
      {children}
    </div>
  );
};
